import * as Sentry from "@sentry/react";
import React, { lazy, Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PublicRoute } from "./components/PublicRoute";
import { OrgProvider } from "./context";
import { AuthProvider, ToastProvider } from "./hooks";
import "./index.css";

// Lazy-load each page so Vite can code-split the bundle per route.
// Only the code for the current route is downloaded on first load; the rest
// is fetched on demand as the user navigates.
const App = lazy(() => import("./App"));
const LandingPage = lazy(() =>
  import("./pages/LandingPage").then((m) => ({ default: m.LandingPage }))
);
const DocsPage = lazy(() => import("./pages/DocsPage").then((m) => ({ default: m.DocsPage })));
const AwsDocsPage = lazy(() =>
  import("./pages/AwsDocsPage").then((m) => ({ default: m.AwsDocsPage }))
);
const AzureDocsPage = lazy(() =>
  import("./pages/AzureDocsPage").then((m) => ({ default: m.AzureDocsPage }))
);
const GcpDocsPage = lazy(() =>
  import("./pages/GcpDocsPage").then((m) => ({ default: m.GcpDocsPage }))
);
const DemoPage = lazy(() => import("./pages/DemoPage").then((m) => ({ default: m.DemoPage })));
const LoginPage = lazy(() => import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const SignUpPage = lazy(() =>
  import("./pages/SignUpPage").then((m) => ({ default: m.SignUpPage }))
);
const VerifyEmailPage = lazy(() =>
  import("./pages/VerifyEmailPage").then((m) => ({
    default: m.VerifyEmailPage,
  }))
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  }))
);
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  }))
);
const MfaSetupPage = lazy(() =>
  import("./pages/MfaSetupPage").then((m) => ({ default: m.MfaSetupPage }))
);
const OurApproach = lazy(() =>
  import("./pages/OurApproach").then((m) => ({ default: m.OurApproach }))
);
const ContactPage = lazy(() =>
  import("./pages/ContactPage").then((m) => ({ default: m.ContactPage }))
);
const PhDealPage = lazy(() =>
  import("./pages/PhDealPage").then((m) => ({ default: m.PhDealPage }))
);
const PrivacyPolicyPage = lazy(() =>
  import("./pages/PrivacyPolicyPage").then((m) => ({
    default: m.PrivacyPolicyPage,
  }))
);
const TermsOfServicePage = lazy(() =>
  import("./pages/TermsOfServicePage").then((m) => ({
    default: m.TermsOfServicePage,
  }))
);
const CheckoutPage = lazy(() =>
  import("./pages/CheckoutPage").then((m) => ({ default: m.CheckoutPage }))
);
const CheckoutCompletePage = lazy(() =>
  import("./pages/CheckoutCompletePage").then((m) => ({
    default: m.CheckoutCompletePage,
  }))
);
const CompareCloudZeroPage = lazy(() =>
  import("./pages/CompareCloudZeroPage").then((m) => ({
    default: m.CompareCloudZeroPage,
  }))
);
const OnboardingPage = lazy(() =>
  import("./pages/OnboardingPage").then((m) => ({ default: m.OnboardingPage }))
);

const JoinPage = lazy(() => import("./pages/JoinPage").then((m) => ({ default: m.JoinPage })));
const BlogIndexPage = lazy(() =>
  import("./pages/BlogIndexPage").then((m) => ({ default: m.BlogIndexPage }))
);
const UnusedEbsVolumesPost = lazy(() =>
  import("./pages/blog/UnusedEbsVolumes").then((m) => ({ default: m.UnusedEbsVolumesPost }))
);
const NatGatewayNoTrafficPost = lazy(() =>
  import("./pages/blog/NatGatewayNoTraffic").then((m) => ({ default: m.NatGatewayNoTrafficPost }))
);
const S3IncompleteMultipartUploadsPost = lazy(() =>
  import("./pages/blog/S3IncompleteMultipartUploads").then((m) => ({
    default: m.S3IncompleteMultipartUploadsPost,
  }))
);
const ElastiCacheServerlessMigrationPost = lazy(() =>
  import("./pages/blog/ElastiCacheServerlessMigration").then((m) => ({
    default: m.ElastiCacheServerlessMigrationPost,
  }))
);
const IdleDocumentDbInstancesPost = lazy(() =>
  import("./pages/blog/IdleDocumentDbInstances").then((m) => ({
    default: m.IdleDocumentDbInstancesPost,
  }))
);
const EcrMissingLifecyclePolicyPost = lazy(() =>
  import("./pages/blog/EcrMissingLifecyclePolicy").then((m) => ({
    default: m.EcrMissingLifecyclePolicyPost,
  }))
);
const RdsSavingsPlansPost = lazy(() =>
  import("./pages/blog/RdsSavingsPlans").then((m) => ({ default: m.RdsSavingsPlansPost }))
);
const AzureIdleRedisCachePost = lazy(() =>
  import("./pages/blog/AzureIdleRedisCache").then((m) => ({
    default: m.AzureIdleRedisCachePost,
  }))
);
const UnderutilizedDocumentDbInstancesPost = lazy(() =>
  import("./pages/blog/UnderutilizedDocumentDbInstances").then((m) => ({
    default: m.UnderutilizedDocumentDbInstancesPost,
  }))
);

// Initialise Sentry error tracking (only when DSN is provided via build-time env var)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
  });
}

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
            <OrgProvider>
              <Suspense fallback={null}>
                <SentryRoutes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/docs" element={<DocsPage />} />
                  <Route path="/docs/aws" element={<AwsDocsPage />} />
                  <Route path="/docs/azure" element={<AzureDocsPage />} />
                  <Route path="/docs/gcp" element={<GcpDocsPage />} />
                  <Route path="/demo" element={<DemoPage />} />
                  <Route path="/demo/catalog" element={<DemoPage />} />
                  <Route path="/demo/catalog/:findingId" element={<DemoPage />} />
                  <Route
                    path="/login"
                    element={
                      <Suspense
                        fallback={
                          <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-indigo-50/50" />
                        }
                      >
                        <PublicRoute>
                          <LoginPage />
                        </PublicRoute>
                      </Suspense>
                    }
                  />
                  <Route
                    path="/signup"
                    element={
                      <Suspense
                        fallback={
                          <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-indigo-50/50" />
                        }
                      >
                        <PublicRoute>
                          <SignUpPage />
                        </PublicRoute>
                      </Suspense>
                    }
                  />
                  <Route path="/verify" element={<VerifyEmailPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route
                    path="/mfa-setup"
                    element={
                      <ProtectedRoute>
                        <MfaSetupPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <App />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/catalog"
                    element={
                      <ProtectedRoute>
                        <App />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/catalog/:findingId"
                    element={
                      <ProtectedRoute>
                        <App />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/our-approach" element={<OurApproach />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/ph-deal" element={<PhDealPage />} />
                  <Route path="/blog" element={<BlogIndexPage />} />
                  <Route path="/blog/unused-ebs-volumes" element={<UnusedEbsVolumesPost />} />
                  <Route
                    path="/blog/nat-gateway-no-traffic"
                    element={<NatGatewayNoTrafficPost />}
                  />
                  <Route
                    path="/blog/s3-incomplete-multipart-uploads"
                    element={<S3IncompleteMultipartUploadsPost />}
                  />
                  <Route
                    path="/blog/elasticache-serverless-migration"
                    element={<ElastiCacheServerlessMigrationPost />}
                  />
                  <Route
                    path="/blog/idle-documentdb-instances"
                    element={<IdleDocumentDbInstancesPost />}
                  />
                  <Route
                    path="/blog/ecr-missing-lifecycle-policy"
                    element={<EcrMissingLifecyclePolicyPost />}
                  />
                  <Route path="/blog/rds-savings-plans" element={<RdsSavingsPlansPost />} />
                  <Route
                    path="/blog/azure-idle-redis-cache"
                    element={<AzureIdleRedisCachePost />}
                  />
                  <Route
                    path="/blog/underutilized-documentdb-instances"
                    element={<UnderutilizedDocumentDbInstancesPost />}
                  />
                  <Route path="/compare/cloudzero" element={<CompareCloudZeroPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route
                    path="/checkout"
                    element={
                      <ProtectedRoute>
                        <CheckoutPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checkout/complete"
                    element={
                      <ProtectedRoute>
                        <CheckoutCompletePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/onboarding" element={<OnboardingPage />} />
                  <Route
                    path="/team"
                    element={
                      <ProtectedRoute>
                        <App />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/join/:inviteCode"
                    element={
                      <ProtectedRoute>
                        <JoinPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </SentryRoutes>
              </Suspense>
            </OrgProvider>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
